// src/lib/sse.ts: reads a text/event-stream reply to a POST (spec 2.6, 4.4). Frames are separated by a
// blank line; `event:` and `data:` lines; data is JSON. Comment lines (": keepalive") are ignored.
// A JSON reply (for example a fresh brief, `{ record }`) is delivered as one "done" event.
import { ApiError, toApiError } from "./http";

export type StreamEvent = { event: string; data: unknown };

/** Parses complete frames out of `buffer`; returns the events and the unconsumed remainder. */
export function parseSseFrames(buffer: string): { events: StreamEvent[]; rest: string } {
  const events: StreamEvent[] = [];
  const normalized = buffer.replace(/\r\n/g, "\n");
  const frames = normalized.split("\n\n");
  const rest = frames.pop() ?? "";
  for (const frame of frames) {
    let event = "message";
    const data: string[] = [];
    for (const line of frame.split("\n")) {
      if (line.startsWith(":")) continue;
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
    }
    if (data.length === 0 && event === "message") continue;
    const raw = data.join("\n");
    let parsed: unknown = raw;
    try {
      parsed = raw ? JSON.parse(raw) : {};
    } catch {
      parsed = raw;
    }
    events.push({ event, data: parsed });
  }
  return { events, rest };
}

export async function postStream(
  path: string,
  body: unknown,
  onEvent: (e: { event: string; data: unknown }) => void,
  signal?: AbortSignal,
): Promise<void> {
  const init: RequestInit = {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "text/event-stream, application/json" },
    body: JSON.stringify(body ?? {}),
  };
  if (signal) init.signal = signal;
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0, "network", "We couldn't reach the server. Check your connection and try again.");
  }
  if (!res.ok) throw await toApiError(res);
  const type = res.headers.get("content-type") ?? "";
  if (!type.includes("text/event-stream")) {
    onEvent({ event: "done", data: await res.json() });
    return;
  }
  if (!res.body) return;
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += value;
    const { events, rest } = parseSseFrames(buffer);
    buffer = rest;
    for (const e of events) onEvent(e);
  }
  const { events } = parseSseFrames(buffer + "\n\n");
  for (const e of events) onEvent(e);
}
